import { useModalContext, ModalType } from 'modules/templateEditor/context';
import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { Modal, ModalBody, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { SpamReportTable } from './SpamReportTable';
import { useSpamRetestAndPoll } from 'modules/templateEditor/hooks/useSpamRetestAndPoll';
import { SpamReportFooter } from './SpamReportModalFooter';
import { EmptyStateGenerator } from 'modules/templateIndex/components/EmptyStates';
import { ErrorStatePageError } from 'ui/illustrations';

const StyledModalBody = styled(ModalBody)`
display: grid;
gap: ${spacing.plusHalf};
padding: ${spacing.half} 0;
`;

const StyledInfoBanner = styled(InfoBanner)`
padding: 0 ${spacing.base};
`;

export const spamReportModalText = {
  title: 'Spam Report',
  infoBanner:
    'This spam report should only be used in conjunction with email best practices to ensure deliverability.',
  emptyStateTitle: 'We’ve ran into a snag!',
  emptyStateCaption: 'There was an error loading the spam report. Please try again later.',
};

export const SpamReportModal = () => {
  const { closeModal } = useModalContext();
  const queryParams = useQueryParams();
  const templateIdParam = queryParams.get('templateId');
  const templateId =
    templateIdParam && !isNaN(Number(templateIdParam)) ? parseInt(templateIdParam, 10) : undefined;

  const {
    startRetestAndPolling,
    spamReports,
    isPolling,
    cancelPolling,
    isSpamReportError,
    isLoading,
  } = useSpamRetestAndPoll(templateId, { enableInitialData: true });

  const handleCloseModal = () => {
    if (isPolling) {
      cancelPolling();
    }
    closeModal(ModalType.SpamReport);
  };

  return (
    <Modal id="spam-report" withPortal isModalOpen onClose={handleCloseModal}>
      <ModalHeader>{spamReportModalText.title}</ModalHeader>
      <StyledModalBody>
        {isSpamReportError || !templateId ? (
          <EmptyStateGenerator
            id="spam-report-empty-table"
            Image={ErrorStatePageError}
            title={spamReportModalText.emptyStateTitle}
          >
            {spamReportModalText.emptyStateCaption}
          </EmptyStateGenerator>
        ) : (
          <>
            <StyledInfoBanner isSubtle>{spamReportModalText.infoBanner}</StyledInfoBanner>
            <SpamReportTable data={spamReports} isLoading={isLoading} />
          </>
        )}
      </StyledModalBody>
      <SpamReportFooter
        handleCloseModal={handleCloseModal}
        handleRetestAction={startRetestAndPolling}
      />
    </Modal>
  );
};
